// Production loader — single-bundle IIFE (`oma-calc-embed.js`) carregado via
// <script src="https://cdn.omagroup.com.br/embed/oma-calc-embed.js" async></script>
//
// Contrato com o site host (mockup 15 + D10):
//   <link rel="stylesheet" href="https://cdn.omagroup.com.br/embed/oma-calc-embed.css" />
//   <div data-oma-calc data-token="..." data-api-base="https://api.omagroup.com.br"></div>
//   <script src="https://cdn.omagroup.com.br/embed/oma-calc-embed.js" async></script>
//
// Idempotência: cada container marca `data-oma-mounted=1` após montar, evitando
// double-mount em SPAs do host que re-renderizam.

import './styles/globals.css';
import { createRoot } from 'react-dom/client';
import { App } from './App';
import { FullScreenWidget } from './FullScreenWidget';
import { CarbonometerWidget } from './CarbonometerWidget';

const DEFAULT_API_BASE =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE) ||
  'https://api.omagroup.com.br';

type WidgetMode = 'calculator' | 'carbonometer';

interface MountOpts {
  token: string;
  apiBase?: string;
  locale?: string;
  fullScreen?: boolean;
  mode?: WidgetMode;
}

interface OMACalcAPI {
  mount: (container: HTMLElement, opts: MountOpts) => void;
  mountAll: () => void;
}

function mountOne(
  node: HTMLElement,
  token: string,
  apiBase: string,
  locale?: string,
  fullScreen?: boolean,
  mode: WidgetMode = 'calculator',
): void {
  if (node.dataset.omaMounted === '1') return;
  node.dataset.omaMounted = '1';
  if (mode === 'carbonometer') {
    // Painel TV/projeção do carbonômetro (mockup 16, D12). Sempre full-bleed.
    createRoot(node).render(<CarbonometerWidget token={token} apiBase={apiBase} locale={locale} />);
    return;
  }
  if (fullScreen) {
    // Standalone mode: FullScreenWidget aplica bg branded no body e cobre
    // a viewport inteira. Usado pelo host page tester (data-fullscreen="1")
    // e por links "Abrir widget" do admin.
    createRoot(node).render(<FullScreenWidget token={token} apiBase={apiBase} locale={locale} />);
  } else {
    createRoot(node).render(<App token={token} apiBase={apiBase} locale={locale} />);
  }
}

function mountAll(): void {
  const nodes = document.querySelectorAll<HTMLElement>('[data-oma-calc]');
  nodes.forEach((node) => {
    const token = node.dataset.token;
    if (!token) {
      // Silencioso em produção — log só em dev.
      return;
    }
    const apiBase = node.dataset.apiBase ?? DEFAULT_API_BASE;
    const locale = node.dataset.locale;
    const fullScreen = node.dataset.fullscreen === '1';
    const mode: WidgetMode = node.dataset.mode === 'carbonometer' ? 'carbonometer' : 'calculator';
    mountOne(node, token, apiBase, locale, fullScreen, mode);
  });
}

const api: OMACalcAPI = {
  mount(container, opts) {
    mountOne(
      container,
      opts.token,
      opts.apiBase ?? DEFAULT_API_BASE,
      opts.locale,
      opts.fullScreen,
      opts.mode,
    );
  },
  mountAll,
};

// Expõe global (script é IIFE, name "OMACalc" via vite.config.ts).
// Sites host que precisarem montar dinamicamente em SPAs podem chamar
// window.OMACalc.mount(node, { token: '...' }) após render.
(globalThis as unknown as { OMACalc?: OMACalcAPI }).OMACalc = api;

// Auto-mount no boot: corre se o DOM já estiver pronto, senão espera.
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', mountAll, { once: true });
} else {
  mountAll();
}

export default api;
